import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSocket } from './SocketContext';
import { useAuth } from '../context/AuthContext';
import { getRules } from '../services/rulesService';

export const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  // --- Estado de Autenticación ---
  const { currentUser, loading } = useAuth();
  const { socket } = useSocket();

  // --- Estado Global de Datos ---
  const [rules, setRules] = useState([]);
  const [rulesLoaded, setRulesLoaded] = useState(false); // 🔥 Flag para evitar recargas innecesarias
  const [logs, setLogs] = useState([]);
  const [qr, setQr] = useState("");
  const [pairingCode, setPairingCode] = useState(""); // 🔐 Código de emparejamiento (8 dígitos)
  const [status, setStatus] = useState("Conectando...");
  const [stats, setStats] = useState({});
  const [activeConversations, setActiveConversations] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [botStatusFromFirebase, setBotStatusFromFirebase] = useState(null);

  const addLog = useCallback((message) => {
    setLogs((prev) => [`[${new Date().toLocaleTimeString()}] ${message}`, ...prev.slice(0, 99)]);
  }, []);

  // --- Efecto #1: Gestiona los Sockets ---
  // Se ejecuta una sola vez y se encarga de la comunicación externa.
  useEffect(() => {
    if (!socket) {
      // console.log('⚠️ [DataContext] Socket no disponible');
      return;
    }

    // console.log('🔌 [DataContext] Configurando listeners de socket, estado:', socket.connected ? 'CONECTADO' : 'DESCONECTADO');

    // Handler para status que limpia QR cuando se desconecta
    const handleStatus = (newStatus) => {
      // console.log('📊 [DataContext] Status recibido:', newStatus);
      setStatus(newStatus);
      // Si el bot se desconecta, limpiar el QR y pairing code para que se vea el estado correcto
      if (newStatus === 'disconnected' || newStatus === 'Desconectado') {
        setQr('');
        setPairingCode('');
      }
    };

    // Handler para QR
    const handleQR = (qrCode) => {
      // console.log('📱 [DataContext] QR recibido:', qrCode ? 'QR presente' : 'QR null');
      setQr(qrCode);
    };

    // Handler para reconexión - solicitar status actual
    const handleConnect = () => {
      // console.log('🔌 [DataContext] Socket conectado, solicitando status actual...');
      // Solicitar status actual del bot
      if (currentUser) {
        socket.emit('request_status');
      }
    };

    // Registramos listeners del socket una sola vez
    // 🔐 Handler para Pairing Code (código de 8 dígitos alternativo al QR)
    const handlePairingCode = (code) => {
      // console.log('🔐 [DataContext] Pairing Code recibido:', code);
      setPairingCode(code);
    };

    socket.on("connect", handleConnect);
    socket.on("log", addLog);
    socket.on("status", handleStatus);
    socket.on("qr_code", handleQR);
    socket.on("pairing_code", handlePairingCode);
    socket.on("conversations_update", (data) => {
      if (data && typeof data === 'object') {
        setActiveConversations(data);
      } else {
        setActiveConversations({});
      }
    });

    // Si ya está conectado, solicitar status inmediatamente
    if (socket.connected && currentUser) {
      // console.log('🔌 [DataContext] Socket ya conectado, solicitando status...');
      socket.emit('request_status');
    }

    // Limpieza al desmontar el componente
    return () => {
      socket.off("connect", handleConnect);
      socket.off("log", addLog);
      socket.off("status", handleStatus);
      socket.off("qr_code", handleQR);
      socket.off("pairing_code", handlePairingCode);
      socket.off("conversations_update");
    };
  }, [socket, addLog, currentUser]);

  // --- Efecto #1.1: Conectar/Desconectar socket con token cuando cambia el usuario ---
  useEffect(() => {
    // La conexión y autenticación del socket se gestiona en SocketContext.
    // Aquí no realizamos handshake para evitar duplicidades.
  }, [currentUser]);

  // --- Efecto #2: Carga los Datos Específicos del Usuario ---
  useEffect(() => {
    // Si no hay usuario, no hay datos que cargar. Limpiamos todo.
    if (!currentUser) {
      setRules([]);
      setStats({});
      setBotStatusFromFirebase(null);
      setIsDataLoading(false);
      return;
    }

    setIsDataLoading(true);

    // 🔥 Usar peticion al backend (REST API) en lugar de lectura de Firebase
    const loadRulesOnce = async () => {
      try {
        const response = await getRules();
        if (response.success !== false) {
          const rulesData = response.rules || response;
          if (Array.isArray(rulesData)) {
            setRules(rulesData);
          } else {
            setRules([]);
          }
        } else {
          setRules([]);
        }
        setRulesLoaded(true);
        setIsDataLoading(false);
      } catch (error) {
        setRules([]);
        setIsDataLoading(false);
      }
    };

    loadRulesOnce();

    // 🔄 Escuchar eventos locales para actualizar sin recargar de Firebase
    const handleRuleCreated = (e) => {
      const { rule } = e.detail || {};
      if (rule && rule.id) {
        // 🛡️ Dedup por id: si la regla ya está (entró por optimista + evento/snapshot), no duplicar
        setRules(prev => prev.some(r => r.id === rule.id)
          ? prev
          : [...prev, rule].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
      }
    };

    const handleRuleUpdated = (e) => {
      const { ruleId, updates } = e.detail || {};
      if (ruleId) {
        setRules(prev => prev.map(r => r.id === ruleId ? { ...r, ...updates } : r));
        // console.log(`✏️ [DataContext] Regla actualizada localmente: ${ruleId}`);
      }
    };

    const handleRuleDeleted = (e) => {
      const { ruleId, childrenIds = [] } = e.detail || {};
      if (ruleId) {
        const idsToRemove = [ruleId, ...childrenIds];
        setRules(prev => prev.filter(r => !idsToRemove.includes(r.id)));
        // console.log(`🗑️ [DataContext] Regla(s) eliminada(s) localmente: ${idsToRemove.join(', ')}`);
      }
    };

    const handleRulesReload = () => {
      // console.log('🔄 [DataContext] Recarga de reglas solicitada');
      loadRulesOnce();
    };

    window.addEventListener('rule_created', handleRuleCreated);
    window.addEventListener('rule_updated', handleRuleUpdated);
    window.addEventListener('rule_deleted', handleRuleDeleted);
    window.addEventListener('rules_reload', handleRulesReload);

    // Limpiamos los listeners cuando el usuario cambia o cierra sesión.
    return () => {
      // 🔥 Remover listeners de eventos locales
      window.removeEventListener('rule_created', handleRuleCreated);
      window.removeEventListener('rule_updated', handleRuleUpdated);
      window.removeEventListener('rule_deleted', handleRuleDeleted);
      window.removeEventListener('rules_reload', handleRulesReload);
    };
  }, [currentUser]); // ❌ NO incluir socket - es independiente

  // ⚡ OPTIMIZACIÓN CRÍTICA: Memoizar el valor del context para evitar re-renders masivos
  // Esto evita que TODOS los componentes que usan DataContext se re-rendericen innecesariamente
  const value = useMemo(() => ({
    user: currentUser,
    rules: Array.isArray(rules) ? rules : [],
    logs: Array.isArray(logs) ? logs : [],
    qr: typeof qr === 'string' ? qr : "",
    pairingCode: typeof pairingCode === 'string' ? pairingCode : "", // 🔐 Código de emparejamiento
    status: typeof status === 'string' ? status : "Conectando...",
    stats: stats && typeof stats === 'object' ? stats : {},
    activeConversations: activeConversations && typeof activeConversations === 'object' ? activeConversations : {},
    isLoading: loading || isDataLoading,
    addLog,
    setLogs
  }), [
    currentUser,
    rules,
    logs,
    qr,
    pairingCode,
    status,
    stats,
    activeConversations,
    loading,
    isDataLoading,
    addLog
    // setLogs es estable (useState setter), no necesita estar en dependencias
  ]);

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
